import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, RotateCcw, ChevronRight } from 'lucide-react';

const labelOf = (c) => c.label || c.text || '';

const DecisionTreeElement = ({ config, element, onComplete, onSaveData, isCompleted }) => {
  const dt = config.decision_tree || config || {};
  const nodes = dt.nodes || [];
  const startId = nodes.find(n => n.id === 'start') ? 'start' : nodes[0]?.id;
  const [currentNodeId, setCurrentNodeId] = useState(startId);
  const [history, setHistory] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [picked, setPicked] = useState(null); // { index, choice, correct }

  const currentNode = nodes.find(n => n.id === currentNodeId) || nodes[0];
  const graded = (currentNode?.choices || []).some(c => c.is_correct);

  const finish = (finalNodeId, ok, path) => {
    setCompleted(true);
    onSaveData({ history: path, final_node: finalNodeId, is_correct: ok });
    if (ok && !isCompleted) onComplete();
  };

  const advance = (choice) => {
    const nextId = choice.next_node ?? choice.next;
    const path = [...history, { nodeId: currentNodeId, choiceText: labelOf(choice) }];
    setHistory(path);
    setPicked(null);
    const nextNode = nodes.find(n => n.id === nextId);
    if (!nextId || !nextNode) { finish(currentNodeId, graded ? !!choice.is_correct : true, path); return; }
    setCurrentNodeId(nextId);
    if (nextNode.is_end || !(nextNode.choices || []).length) finish(nextId, nextNode.is_correct !== false, path);
  };

  const handleChoice = (choice, index) => {
    if (graded || choice.feedback) { setPicked({ index, choice, correct: graded ? !!choice.is_correct : true }); return; }
    advance(choice);
  };

  const handleRestart = () => { setCurrentNodeId(startId); setHistory([]); setCompleted(false); setPicked(null); };

  const isEnd = currentNode?.is_end || !(currentNode?.choices || []).length;
  const isEndCorrect = isEnd && currentNode?.is_correct !== false;
  const isEndIncorrect = isEnd && currentNode?.is_correct === false;
  const finishedOnChoice = completed && !isEnd;

  return (
    <div className="space-y-4" data-testid="decision-tree-element">
      {dt.title && <h3 className="text-base font-semibold text-[#2a3e5d]">{dt.title}</h3>}
      {dt.scenario && !completed && <p className="text-sm text-slate-600 italic bg-slate-50 p-3 rounded-lg">{dt.scenario}</p>}

      {history.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-xs text-slate-400" data-testid="decision-path">
          {history.map((h, i) => (
            <span key={`hist-${i}`} className="flex items-center gap-1">
              <span className="bg-slate-100 px-2 py-0.5 rounded">{h.choiceText}</span>
              {i < history.length - 1 && <span>&rarr;</span>}
            </span>
          ))}
        </div>
      )}

      {currentNode && (
        <div className={`p-5 border-2 rounded-xl transition-colors ${isEndCorrect || finishedOnChoice ? 'border-green-300 bg-green-50' : isEndIncorrect ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`}>
          <p className="text-sm text-slate-800 leading-relaxed">{currentNode.text}</p>

          {(isEndCorrect || finishedOnChoice) && (
            <div className="mt-4 p-3 bg-green-100 rounded-lg flex items-center gap-2" data-testid="decision-correct">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-green-800">Scenario complete!</p>
                <p className="text-xs text-green-700 mt-0.5">Well done — you made the right decisions.</p>
              </div>
            </div>
          )}

          {isEndIncorrect && (
            <div className="mt-4 p-3 bg-red-100 rounded-lg" data-testid="decision-incorrect">
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-800">Not the best outcome</p>
                  <p className="text-xs text-red-700 mt-0.5">Try a different approach — think about what you've learned.</p>
                </div>
              </div>
              <Button onClick={handleRestart} variant="outline" size="sm" className="mt-3 gap-1.5 border-red-200 text-red-700 hover:bg-red-50 min-h-[44px]" data-testid="retry-decision-btn">
                <RotateCcw className="h-3.5 w-3.5" /> Try Again
              </Button>
            </div>
          )}

          {(isEndCorrect || finishedOnChoice) && (
            <Button onClick={handleRestart} variant="outline" size="sm" className="mt-3 gap-1.5" data-testid="restart-scenario-btn">
              <RotateCcw className="h-3.5 w-3.5" /> Explore a different path
            </Button>
          )}

          {!isEnd && !completed && (
            <div className="mt-4 space-y-2">
              {(currentNode.choices || []).map((choice, ci) => {
                const isPicked = picked?.index === ci;
                return (
                  <button
                    key={`choice-${ci}`}
                    type="button"
                    disabled={!!picked}
                    onClick={() => handleChoice(choice, ci)}
                    className={`w-full text-left p-3 min-h-[44px] rounded-lg border-2 transition-colors text-sm font-medium ${
                      isPicked ? (picked.correct ? 'border-green-500 bg-green-50 text-green-900' : 'border-red-400 bg-red-50 text-red-900')
                        : 'border-slate-200 hover:border-[#20acf6] hover:bg-[#20acf6]/5 text-[#2a3e5d] disabled:opacity-60 disabled:hover:border-slate-200 disabled:hover:bg-transparent'
                    }`}
                    data-testid={`choice-${ci}`}
                  >
                    <span className="font-bold mr-2 text-slate-400">{String.fromCharCode(65 + ci)}</span>{labelOf(choice)}
                  </button>
                );
              })}

              {picked && (
                <div className={`mt-2 p-3 rounded-lg border ${picked.correct ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`} data-testid="decision-choice-feedback" data-result={picked.correct ? 'correct' : 'incorrect'}>
                  <p className={`text-sm font-semibold flex items-center gap-1.5 ${picked.correct ? 'text-green-800' : 'text-red-700'}`}>
                    {picked.correct ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    {picked.correct ? '✓ Correct!' : '✗ Not quite — try again'}
                  </p>
                  {picked.choice.feedback && <p className="text-sm text-slate-700 mt-1" data-testid="decision-feedback-text">{picked.choice.feedback}</p>}
                  {picked.correct ? (
                    <Button size="sm" className="mt-3 bg-green-600 hover:bg-green-700 min-h-[44px]" onClick={() => advance(picked.choice)} data-testid="decision-continue-btn">
                      Continue <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="mt-3 border-red-200 text-red-700 min-h-[44px]" onClick={() => setPicked(null)} data-testid="decision-try-again-btn">
                      <RotateCcw className="h-4 w-4 mr-1" /> Try again
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DecisionTreeElement;
